import React, { useEffect, useState } from 'react';
import { fetchAIInsights } from '../api';
import { Sparkles, TrendingUp, Shield, DollarSign, Target, ArrowRight } from 'lucide-react';

const AIInsightsWidget = ({ userId, useCaseId = 'wealth_management' }) => {
    const [insights, setInsights] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const getBorderColor = (type) => {
        switch (type) {
            case 'risk_management':
            case 'risk_reduction':
            case 'health_improvement':
                return 'border-emerald-500/70';
            case 'sector_concentration':
            case 'rebalancing':
                return 'border-emerald-500/60';
            case 'growth_opportunity':
            case 'market_outlook':
            case 'income_enhancement':
                return 'border-emerald-500/80';
            default:
                return 'border-emerald-500/70';
        }
    };

    const getCategoryLabel = (type) => {
        switch (type) {
            case 'risk_management':
            case 'risk_reduction':
            case 'health_improvement':
                return 'RISK WARNING';
            case 'sector_concentration':
            case 'rebalancing':
                return 'SECTOR BALANCE';
            case 'growth_opportunity':
            case 'income_enhancement':
                return 'OPPORTUNITY';
            case 'tax_optimization':
                return 'TAX STRATEGY';
            case 'premium_services':
            case 'estate_planning':
                return 'ADVISORY SERVICE';
            default:
                return 'INSIGHT';
        }
    };

    useEffect(() => {
        const getAIInsights = async () => {
            try {
                setIsLoading(true);
                const data = await fetchAIInsights(userId, useCaseId);
                setInsights(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error fetching AI insights:", err);
                setError("Failed to load AI insights");
            } finally {
                setIsLoading(false);
            }
        };

        getAIInsights();
    }, [userId, useCaseId]);

    const handleActionClick = (insight) => {
        console.log('Action clicked:', insight);
        // Could open modal, navigate to service page, or schedule appointment
    };

    return (
        <div
            className="bg-gray-800/30 backdrop-blur-md rounded-lg relative overflow-hidden h-full flex flex-col"
            style={{
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(0, 0, 0, 0.05), inset 0 0 0 1px rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.18)'
            }}
        >
            {/* Decorative elements for glass effect */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-emerald-500/8 rounded-full blur-3xl"></div>

            {/* Content container */}
            <div className="relative z-10 p-4 flex flex-col h-full">
                <div className="flex items-center mb-4 flex-shrink-0">
                    <Sparkles size={20} className="text-emerald-400 mr-2" />
                    <h2 className="text-lg font-medium text-white">AI Insights</h2>
                    <div className="ml-auto flex items-center">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                        <span className="text-xs text-white">Live</span>
                    </div>
                </div>
                {isLoading ? (
                    <div className="animate-pulse space-y-3 flex-1 overflow-hidden">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-20 bg-gray-700 rounded"></div>
                        ))}
                    </div>
                ) : error ? (
                    <div className="text-center text-red-500 py-4 flex-1">
                        {error}
                    </div>
                ) : (
                    <div className="space-y-3 flex-1 overflow-y-auto pr-1 scrollbar-thin">
                        {insights.slice(0, 4).map((insight, index) => (
                            <div
                                key={index}
                                className={`bg-slate-900/50 backdrop-blur-sm border-l-4 ${getBorderColor(insight.type)} rounded-r-lg p-3 relative overflow-hidden group hover:bg-slate-900/70 transition-all duration-200 cursor-pointer`}
                                onClick={() => handleActionClick(insight)}
                            >
                                {/* Category label */}
                                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                                    {getCategoryLabel(insight.type)}
                                </div>
                                
                                {/* Description */}
                                <p className="text-xs text-white leading-relaxed mb-2.5">
                                    {insight.description}
                                </p>

                                {/* Action link */}
                                <button className="flex items-center gap-1 text-[11px] text-emerald-400 hover:text-emerald-300 font-medium transition-all group-hover:gap-2">
                                    {insight.action}
                                    <ArrowRight size={12} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AIInsightsWidget;